import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import * as Mutation from '../../../api/gql/mutation'
import { calendarFor, type CalendarLike } from '../lib/calendar'
import { activityHitBox, computeResizePreview, hitTestLinkTarget, isRefusedLinkDrop, resizeFinishDayAt } from '../lib/drag-geometry'
import { LINK_HANDLE_GAP } from '../lib/constants'
import { dateAt, dayIndex, rowCenterY, type BarBox, type DateRange } from '../lib/geometry'
import type { VisibleRow } from '../lib/tree'
import type { RunFn } from '../detail/gantt-ops'
import { useGanttStore, useGanttStoreApi, type DragState } from '../store/ganttStore'
import type { ScheduleActivity, ScheduleCalendar, ScheduleExternalActivity } from '../types'

export interface ResizePreview { activityId: string; x: number; width: number; label: string }
export interface LinkPreview { x1: number; y1: number; x2: number; y2: number; targetBox: BarBox | null; refused: boolean }

export interface BarDragApi {
  drag: DragState
  onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void
  onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => void
  onPointerUp: (e: React.PointerEvent<SVGSVGElement>) => void
  onPointerCancel: (e: React.PointerEvent<SVGSVGElement>) => void
  resizePreview: ResizePreview | null
  linkPreview: LinkPreview | null
}

interface Deps {
  scheduleId: string
  canEdit: boolean
  byId: Map<string, ScheduleActivity>
  rows: VisibleRow<ScheduleActivity>[]
  rowIndexById: Map<string, number>
  ghostsByRow: Map<string, ScheduleExternalActivity[]>
  range: DateRange
  pxPerDay: number
  calendarsById: Map<string, ScheduleCalendar>
  defaultCalendarId: string
  scrollLeft: number
  scrollTop: number
  run: RunFn
}

/** A raw pointer position captured on every native pointermove, replayed at most once per animation
 * frame (fix round 1) — only the primitives needed to recompute content coordinates later, never the
 * `PointerEvent` itself (no reliance on React not recycling it). */
interface PendingMove { clientX: number; clientY: number; target: SVGSVGElement }

/**
 * Owns the whole Task 18 drag lifecycle — bar resize by dragging a `TaskBar`'s right-edge handle,
 * and dependency creation by dragging a link handle onto another bar (spec B §9.6). Reads/writes
 * the store's `drag` (so `GanttTimeline` can render the live preview without owning the gesture
 * itself) and commits through `run` on pointerup. `GanttTimeline` forwards its own pointer events
 * here first (routed by `[data-resize-handle]`/`[data-link-handle]`), falling back to its pan
 * gesture only while `drag` is null.
 *
 * Fix round 1: `onPointerMove` no longer writes to the store on every native event — spec §17
 * requires pointer moves during a drag to be rAF-throttled, mirroring `useGanttScroll.ts`'s
 * scroll-event pattern. The raw handler only stashes the latest pointer position in a ref and
 * schedules one frame; `applyPendingMove` (the actual `finishDay`/`hitTestLinkTarget`/`setDrag`
 * work) runs once per frame from there. `endDrag` cancels any pending frame and, on a commit
 * (pointerup), flushes it synchronously first so the committed value always reflects the true final
 * pointer position, not a stale pre-frame one.
 */
export const useBarDrag = ({ scheduleId, canEdit, byId, rows, rowIndexById, ghostsByRow, range, pxPerDay, calendarsById, defaultCalendarId, scrollLeft, scrollTop, run }: Deps): BarDragApi => {
  const { t } = useTranslation()
  const api = useGanttStoreApi()
  const drag = useGanttStore((s) => s.drag)
  const [updateActivities] = useMutation(Mutation.UPDATE_SCHEDULE_ACTIVITIES)
  const [createDependency] = useMutation(Mutation.CREATE_SCHEDULE_DEPENDENCY)

  const pendingMoveRef = useRef<PendingMove | null>(null)
  const moveFrameRef = useRef<number | null>(null)

  const contentPoint = useCallback((clientX: number, clientY: number, target: SVGSVGElement) => {
    const rect = target.getBoundingClientRect()
    return { x: clientX - rect.left + scrollLeft, y: clientY - rect.top + scrollTop }
  }, [scrollLeft, scrollTop])

  // The one place that actually computes and writes the drag's live position — called once per
  // animation frame from `flushPendingMove`, and once more, synchronously, from `endDrag` to apply
  // whatever moved since the last frame before a commit reads `drag`.
  const applyMove = useCallback((clientX: number, clientY: number, target: SVGSVGElement) => {
    const st = api.getState()
    const d = st.drag
    if (!d) return
    const p = contentPoint(clientX, clientY, target)
    if (d.kind === 'resize') {
      const a = byId.get(d.activityId)
      if (!a) return
      const minDay = dayIndex(a.start, range.start)
      const finishDay = resizeFinishDayAt(p.x, pxPerDay, minDay)
      if (finishDay !== d.finishDay) st.setDrag({ ...d, finishDay })
      return
    }
    const targetId = hitTestLinkTarget(rows, p.x, p.y, range.start, pxPerDay, d.fromId)
    if (targetId !== d.targetId || p.x !== d.x || p.y !== d.y) st.setDrag({ ...d, x: p.x, y: p.y, targetId })
  }, [api, byId, range.start, pxPerDay, rows, contentPoint])

  const flushPendingMove = useCallback(() => {
    moveFrameRef.current = null
    const pending = pendingMoveRef.current
    pendingMoveRef.current = null
    if (pending) applyMove(pending.clientX, pending.clientY, pending.target)
  }, [applyMove])

  // Cancel any in-flight frame on unmount — nothing depends on it firing after `GanttView` (and
  // this hook) is gone.
  useEffect(() => () => {
    if (moveFrameRef.current != null) cancelAnimationFrame(moveFrameRef.current)
  }, [])

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!canEdit || api.getState().pendingCount > 0) return
    const target = e.target as Element
    const resizeEl = target.closest('[data-resize-handle]')
    if (resizeEl) {
      const activityId = resizeEl.getAttribute('data-activity-id')
      const a = activityId ? byId.get(activityId) : null
      if (!a) return
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      api.getState().setDrag({ kind: 'resize', activityId: a.id, finishDay: dayIndex(a.finish, range.start) })
      return
    }
    const linkEl = target.closest('[data-link-handle]')
    if (linkEl) {
      const activityId = linkEl.getAttribute('data-activity-id')
      if (!activityId) return
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      const p = contentPoint(e.clientX, e.clientY, e.currentTarget)
      api.getState().setDrag({ kind: 'link', fromId: activityId, x: p.x, y: p.y, targetId: null })
    }
  }, [canEdit, api, byId, range.start, contentPoint])

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!api.getState().drag) return
    pendingMoveRef.current = { clientX: e.clientX, clientY: e.clientY, target: e.currentTarget }
    if (moveFrameRef.current == null) moveFrameRef.current = requestAnimationFrame(flushPendingMove)
  }, [api, flushPendingMove])

  const endDrag = useCallback((e: React.PointerEvent<SVGSVGElement>, commit: boolean) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (moveFrameRef.current != null) { cancelAnimationFrame(moveFrameRef.current); moveFrameRef.current = null }
    const pending = pendingMoveRef.current
    pendingMoveRef.current = null
    if (commit && pending) applyMove(pending.clientX, pending.clientY, pending.target)

    const d = api.getState().drag
    if (!d) return
    api.getState().setDrag(null)
    if (!commit) return

    if (d.kind === 'resize') {
      const a = byId.get(d.activityId)
      if (!a) return
      const cal: CalendarLike | null = calendarFor(a, calendarsById, defaultCalendarId)
      const finishIso = dateAt(d.finishDay, range.start)
      const preview = computeResizePreview(cal, a.start, finishIso)
      if (preview === a.durationDays || preview < 1) return
      run(
        () => updateActivities({ variables: { scheduleId, patches: [{ id: a.id, durationDays: preview }] } }) as unknown as Promise<{ data: any }>,
        (data: any) => data.updateScheduleActivities,
      ).catch(() => { /* run() already toasted */ })
      return
    }

    if (d.targetId) {
      run(
        () => createDependency({ variables: { scheduleId, input: { predecessorId: d.fromId, successorId: d.targetId, type: 'FS', lagDays: 0 } } }) as unknown as Promise<{ data: any }>,
        (data: any) => data.createScheduleDependency,
      ).catch(() => { /* run() already toasted */ })
      return
    }
    if (isRefusedLinkDrop(rows, ghostsByRow, d.x, d.y, range.start, pxPerDay, d.fromId)) {
      toastr.error(t('Common_error'), t('Schedule_link_drop_refused'))
    }
  }, [api, applyMove, byId, calendarsById, defaultCalendarId, range.start, pxPerDay, rows, ghostsByRow, run, scheduleId, updateActivities, createDependency, t])

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => endDrag(e, true), [endDrag])
  const onPointerCancel = useCallback((e: React.PointerEvent<SVGSVGElement>) => endDrag(e, false), [endDrag])

  const resizePreview = useMemo<ResizePreview | null>(() => {
    if (!drag || drag.kind !== 'resize') return null
    const a = byId.get(drag.activityId)
    if (!a) return null
    const cal = calendarFor(a, calendarsById, defaultCalendarId)
    const finishIso = dateAt(drag.finishDay, range.start)
    const preview = computeResizePreview(cal, a.start, finishIso)
    const startDay = dayIndex(a.start, range.start)
    const x = startDay * pxPerDay
    const width = Math.max(2, (drag.finishDay - startDay + 1) * pxPerDay)
    return { activityId: a.id, x, width, label: t('Schedule_resize_preview', { from: a.durationDays, to: preview }) }
  }, [drag, byId, calendarsById, defaultCalendarId, range.start, pxPerDay, t])

  const linkPreview = useMemo<LinkPreview | null>(() => {
    if (!drag || drag.kind !== 'link') return null
    const from = byId.get(drag.fromId)
    const fromIndex = rowIndexById.get(drag.fromId)
    if (!from || fromIndex == null) return null
    const fromBox = activityHitBox(from, range.start, pxPerDay)
    // matches the handle's own rendered position (`TaskBar`/`MilestoneMarker`: box right edge + LINK_HANDLE_GAP)
    const x1 = fromBox.x + fromBox.width + LINK_HANDLE_GAP
    const y1 = rowCenterY(fromIndex)
    const target = drag.targetId ? byId.get(drag.targetId) : null
    const targetBox = target ? activityHitBox(target, range.start, pxPerDay) : null
    // Fix round 1: the live "Cannot link here" label is now gated on the same refusal predicate the
    // drop toast already uses, not merely "no target yet" — plain empty background no longer trips it.
    const refused = !targetBox && isRefusedLinkDrop(rows, ghostsByRow, drag.x, drag.y, range.start, pxPerDay, drag.fromId)
    return { x1, y1, x2: drag.x, y2: drag.y, targetBox, refused }
  }, [drag, byId, rowIndexById, range.start, pxPerDay, rows, ghostsByRow])

  // Memoised so `GanttTimeline` (React.memo) doesn't re-render on every `GanttView` render (e.g.
  // every scroll frame) just because this hook returned a fresh object literal — only when one of
  // its own pieces actually changed identity. Now that `onPointerMove` no longer writes to the
  // store per native event (rAF-throttled above), `drag`/`resizePreview`/`linkPreview` — and
  // therefore this object — change at most once per animation frame during a drag too.
  return useMemo<BarDragApi>(
    () => ({ drag, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, resizePreview, linkPreview }),
    [drag, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, resizePreview, linkPreview],
  )
}
